const OCTET = "(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)";
const IPV4_REGEX = new RegExp(`^${OCTET}(\\.${OCTET}){3}$`);
const CIDR_REGEX = new RegExp(`^${OCTET}(\\.${OCTET}){3}/(3[0-2]|[12]?\\d)$`);

const ipToNumber = (ip: string): number => ip.split(".").reduce((acc, octet) => acc * 256 + Number(octet), 0);

// Validates an IPv4 address (e.g. 192.168.1.1); IPv6 is not supported
export const validIp = (ip?: string): boolean => Boolean(ip && (ip === "localhost" || IPV4_REGEX.test(ip)));

// Validates an IPv4 range in CIDR (e.g. 192.168.1.0/24) or dash notation (e.g. 192.168.1.1-192.168.1.10)
export const validIpRange = (range?: string): boolean => {
  if (!range) {
    return false;
  }

  // CIDR notation
  if (range.includes("/")) {
    return CIDR_REGEX.test(range);
  }

  // Dash notation: start and end must be valid IPv4s and the range must be ascending
  const [startIp, endIp, ...rest] = range.split("-");
  return Boolean(
    !rest.length &&
    startIp &&
    endIp &&
    IPV4_REGEX.test(startIp) &&
    IPV4_REGEX.test(endIp) &&
    ipToNumber(startIp) <= ipToNumber(endIp),
  );
};

const LOOPBACK_START = ipToNumber("127.0.0.0");
const LOOPBACK_END = ipToNumber("127.255.255.255");

const cidrToRange = (base: number, prefix: number): [number, number] => {
  const size = 2 ** (32 - prefix);
  const start = Math.floor(base / size) * size;
  return [start, start + size - 1];
};

const overlapsLoopback = (start: number, end: number): boolean => start <= LOOPBACK_END && end >= LOOPBACK_START;

// Checks whether a value is a loopback address ("localhost" or 127.0.0.0/8); a range matches if it covers any loopback IP
export const isLocalIp = (value?: string): boolean => {
  if (!value) {
    return false;
  }
  if (value === "localhost") {
    return true;
  }

  // CIDR notation
  if (value.includes("/")) {
    const [ip, prefix] = value.split("/");
    if (!prefix || !IPV4_REGEX.test(ip)) {
      return false;
    }
    const [start, end] = cidrToRange(ipToNumber(ip), Number(prefix));
    return overlapsLoopback(start, end);
  }

  // Dash notation
  if (value.includes("-")) {
    const [startIp, endIp] = value.split("-");
    if (!IPV4_REGEX.test(startIp) || !IPV4_REGEX.test(endIp)) {
      return false;
    }
    return overlapsLoopback(ipToNumber(startIp), ipToNumber(endIp));
  }

  return IPV4_REGEX.test(value) && ipToNumber(value) >= LOOPBACK_START && ipToNumber(value) <= LOOPBACK_END;
};

// Validates an IPv4 address, "localhost", or an IPv4 range in CIDR/dash notation
export const validIpOrRange = (value?: string): boolean => validIp(value) || validIpRange(value);
